import { useEffect, useState, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  GripVertical,
  Plus,
  Trash2,
  GitBranch,
  SlidersHorizontal,
  Settings2,
} from 'lucide-react';
import { reorderList } from '../../utils/reorder';
import {
  canRemoveQuizSlot,
  formatDurationMinutes,
  getQuizDurationMinutes,
  getQuizSlotPolicyLabel,
  getQuizSummary,
  getTopicBlockDuration,
  type QuizSlot,
  type SessionDurationBreakdown,
  type SubtopicItem,
  type TimelineTopicItem,
  type TopicQuizConfig,
} from '../../types/topicQuiz';
import { questionBankOptions } from '../../data/topicPlannerConfig';
import { TopicDurationField } from '../shared/TopicDurationField';
import { DateTimeField } from '../schedule/DateTimeField';
import { isTopicDurationUnset } from '../../utils/topicDurationHistory';
import { TopicQuizModal } from './TopicQuizModal';
import {
  fetchCodeliveQuizDefaults,
  quizConfigFromGlobalDefaults,
  saveCodeliveQuizDefaults,
  type CodeliveQuizDefaults,
} from '../../utils/codeliveQuizDefaults';
import {
  addMinutesToDateTime,
  mergeDayTimings,
  type WorkshopDayTiming,
} from '../../utils/workshopSessionUtils';

interface TopicTimelineProps {
  topics: TimelineTopicItem[];
  duration: SessionDurationBreakdown;
  onTopicsChange: (topics: TimelineTopicItem[]) => void;
  courseId?: string;
  /** Parent recalculates duration when global rules change */
  onQuizDefaultsChange?: (defaults: CodeliveQuizDefaults) => void;
  /** Workshop day-wise planner */
  workshopMode?: boolean;
  workshopDayCount?: number;
  workshopStartDate?: string;
  workshopStartTime?: string;
  daySchedules?: WorkshopDayTiming[];
  onDayScheduleChange?: (
    dayIndex: number,
    patch: { startDate?: string; startTime?: string },
  ) => void;
  onChangeTopicDay?: (topicId: string, dayIndex: number) => void;
  /** Course plan: lock topic/subtopic structure; durations still editable. */
  structureLocked?: boolean;
}

type QuizEditorState = {
  slot: QuizSlot;
  config: TopicQuizConfig;
};

function QuizBranchNode({
  quiz,
  durationMinutes,
  onConfigure,
  onRemove,
}: {
  quiz: TopicQuizConfig;
  durationMinutes: number;
  onConfigure: () => void;
  onRemove?: () => void;
}) {
  const slotLabel = quiz.slot === 'pre' ? 'Pre-Quiz' : 'Post-Quiz';
  const policyLabel = getQuizSlotPolicyLabel(quiz.slot);
  const policyClass =
    quiz.slot === 'pre'
      ? 'bg-secondary-subtle text-secondary'
      : 'bg-danger-subtle text-danger';
  const modeLabel = 'Shared rules';

  return (
    <div className="relative ml-4 flex items-start gap-3 py-1 sm:ml-6">
      <div className="absolute -left-4 top-0 bottom-1/2 w-3 border-b-2 border-l-2 border-primary-subtle rounded-bl-lg sm:-left-6 sm:w-4" />
      <div className="flex flex-1 flex-col gap-2 rounded border border-primary-subtle bg-primary-subtle px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <button
          type="button"
          onClick={onConfigure}
          className="flex min-w-0 flex-1 items-start gap-2 text-start border-0 bg-transparent p-0"
          aria-label={`Edit shared ${slotLabel} rules`}
        >
          <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="badge text-bg-primary">{slotLabel}</span>
              <span className={`badge ${policyClass}`}>{policyLabel}</span>
              <span className="badge text-bg-light text-dark border">
                <SlidersHorizontal size={12} className="me-1" />
                {modeLabel}
              </span>
              <span className="badge text-bg-success-subtle text-success">
                <Clock size={12} className="me-1" />
                {durationMinutes}m
              </span>
            </div>
            <p className="truncate small text-muted mb-0 mt-1">
              {getQuizSummary(quiz, questionBankOptions)}
            </p>
          </div>
        </button>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={onConfigure}
            className="btn btn-sm btn-link text-primary p-1"
            aria-label={`Edit shared ${slotLabel} rules`}
          >
            <Settings2 size={14} />
          </button>
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              className="btn btn-sm btn-link text-danger p-1"
              aria-label={`Remove ${slotLabel}`}
            >
              <Trash2 size={14} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SubtopicList({
  subtopics,
  onChange,
  structureLocked = false,
}: {
  subtopics: SubtopicItem[];
  onChange: (next: SubtopicItem[]) => void;
  structureLocked?: boolean;
}) {
  const addSubtopic = () => {
    if (structureLocked) return;
    const nextIndex = subtopics.length;
    onChange([
      ...subtopics,
      {
        id: `sub-${Date.now()}-${nextIndex}`,
        index: nextIndex,
        title: '',
        durationMinutes: 0,
      },
    ]);
  };

  const updateSubtopic = (id: string, patch: Partial<SubtopicItem>) => {
    onChange(subtopics.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const removeSubtopic = (id: string) => {
    if (structureLocked) return;
    onChange(
      subtopics
        .filter((s) => s.id !== id)
        .map((s, i) => ({ ...s, index: i })),
    );
  };

  return (
    <div className="ms-4 mt-2 d-flex flex-column gap-1">
      {subtopics.map((sub) => (
        <div
          key={sub.id}
          className="d-flex align-items-center gap-2 rounded border bg-light px-2 py-1"
        >
          <span className="text-muted small" style={{ width: 20, textAlign: 'center' }}>
            {sub.index + 1}
          </span>
          {structureLocked ? (
            <span className="small flex-grow-1" style={{ maxWidth: 220 }}>
              {sub.title || 'Subtopic'}
            </span>
          ) : (
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 220 }}
            placeholder="Subtopic title"
            value={sub.title}
            onChange={(e) => updateSubtopic(sub.id, { title: e.target.value })}
          />
          )}
          <div className="d-flex align-items-center gap-1">
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 64 }}
              min={1}
              value={sub.durationMinutes}
              onChange={(e) =>
                updateSubtopic(sub.id, {
                  durationMinutes: Math.max(1, parseInt(e.target.value) || 1),
                })
              }
            />
            <span className="text-muted small">min</span>
          </div>
          {!structureLocked && (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-0 ms-auto"
            onClick={() => removeSubtopic(sub.id)}
            aria-label={`Remove subtopic ${sub.title || sub.index + 1}`}
          >
            <Trash2 size={14} />
          </button>
          )}
        </div>
      ))}
      {!structureLocked && (
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 mt-1"
        style={{ width: 'fit-content' }}
        onClick={addSubtopic}
      >
        <Plus size={14} /> Add subtopic
      </button>
      )}
    </div>
  );
}

export function TopicTimeline({
  topics,
  duration,
  onTopicsChange,
  courseId,
  onQuizDefaultsChange,
  workshopMode = false,
  workshopDayCount = 1,
  workshopStartDate = '',
  workshopStartTime = '',
  daySchedules,
  onDayScheduleChange,
  onChangeTopicDay,
  structureLocked = false,
}: TopicTimelineProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [quizEditor, setQuizEditor] = useState<QuizEditorState | null>(null);
  const [quizDefaults, setQuizDefaults] = useState<CodeliveQuizDefaults | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const defaults = await fetchCodeliveQuizDefaults();
      if (!cancelled) {
        setQuizDefaults(defaults);
        onQuizDefaultsChange?.(defaults);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onQuizDefaultsChange]);

  const displayQuiz = (slot: QuizSlot): TopicQuizConfig =>
    quizConfigFromGlobalDefaults(slot, quizDefaults);

  const clearDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleTopicDrop = (toIndex: number) => {
    if (structureLocked) {
      clearDrag();
      return;
    }
    if (dragIndex === null || dragIndex === toIndex) {
      clearDrag();
      return;
    }
    const reordered = reorderList(topics, dragIndex, toIndex).map((t, i) => ({
      ...t,
      index: i + 1,
    }));
    onTopicsChange(reordered);
    clearDrag();
  };

  const updateTopic = (topicId: string, updater: (t: TimelineTopicItem) => TimelineTopicItem) => {
    onTopicsChange(topics.map((t) => (t.id === topicId ? updater(t) : t)));
  };

  const openQuizEditor = (slot: QuizSlot) => {
    setQuizEditor({
      slot,
      config: quizConfigFromGlobalDefaults(slot, quizDefaults),
    });
  };

  const addPreQuiz = (topicId: string) => {
    // Topic only stores that pre-quiz is enabled — rules are global
    updateTopic(topicId, (t) => ({
      ...t,
      preQuiz: { slot: 'pre', mode: 'level-rules' },
    }));
  };

  const removeQuiz = (topicId: string, slot: QuizSlot) => {
    if (!canRemoveQuizSlot(slot)) return;
    updateTopic(topicId, (t) => {
      const next = { ...t };
      if (slot === 'pre') delete next.preQuiz;
      else delete next.postQuiz;
      return next;
    });
  };

  const saveQuizConfig = async (config: TopicQuizConfig) => {
    if (!quizEditor) return;
    const { slot } = quizEditor;
    // One shared rule set for every session and topic
    try {
      const current = quizDefaults ?? (await fetchCodeliveQuizDefaults());
      const nextDefaults: CodeliveQuizDefaults = {
        ...current,
        [slot]: {
          ...(slot === 'pre' ? current.pre : current.post),
          defaultLevelRules: (config.levelRules || []).map((r) => ({ ...r })),
        },
      };
      const saved = await saveCodeliveQuizDefaults(nextDefaults);
      setQuizDefaults(saved);
      onQuizDefaultsChange?.(saved);
    } catch (err) {
      console.error('Failed to persist CodeLive quiz defaults', err);
    }
  };

  const [expandedSubtopics, setExpandedSubtopics] = useState<Set<string>>(
    () => new Set(topics.filter((t) => t.subtopics?.length).map((t) => t.id)),
  );

  const toggleSubtopics = useCallback((topicId: string) => {
    setExpandedSubtopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) next.delete(topicId);
      else next.add(topicId);
      return next;
    });
  }, []);

  const updateTopicDuration = (topicId: string, durationMinutes: number) => {
    updateTopic(topicId, (t) => ({ ...t, durationMinutes }));
  };

  const updateTopicSubtopics = (topicId: string, subtopics: SubtopicItem[]) => {
    updateTopic(topicId, (t) => ({
      ...t,
      subtopics: subtopics.length > 0 ? subtopics : undefined,
      durationMinutes:
        subtopics.length > 0
          ? subtopics.reduce((sum, s) => sum + s.durationMinutes, 0)
          : t.durationMinutes,
    }));
  };

  const timings = workshopMode
    ? mergeDayTimings(
        workshopDayCount,
        workshopStartDate,
        workshopStartTime,
        daySchedules,
      )
    : [];
  const timingByDay = new Map(timings.map((t) => [t.dayIndex, t]));

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0">
        <div>
          <h6 className="mb-0">
            {workshopMode ? 'Day-wise Topic Planner' : 'Topic Timeline'}
          </h6>
          <p className="mb-0 small text-muted">
            {workshopMode
              ? 'Confirm each day date/time and set subtopic durations (topic time is calculated)'
              : 'Pre/post quiz rules are shared across all sessions and topics'}
          </p>
        </div>
        <div className="text-end">
          <span className="badge text-bg-success">
            {formatDurationMinutes(duration.totalMinutes)} Total
          </span>
          {duration.quizMinutes > 0 && (
            <div className="text-muted small mt-1">
              {formatDurationMinutes(duration.topicMinutes)} topics +{' '}
              {formatDurationMinutes(duration.quizMinutes)} quizzes
            </div>
          )}
        </div>
      </div>

      <div
        className="flex flex-col gap-4"
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setOverIndex(null);
          }
        }}
      >
        {topics.map((topic, index) => {
          const preQuiz = topic.preQuiz ? displayQuiz('pre') : undefined;
          const postQuiz = topic.postQuiz ? displayQuiz('post') : undefined;
          const topicForDuration: TimelineTopicItem = {
            ...topic,
            preQuiz,
            postQuiz,
          };
          const blockMinutes = getTopicBlockDuration(topicForDuration, questionBankOptions);
          const hasQuizTime = blockMinutes > topic.durationMinutes;
          const isDragging = dragIndex === index;
          const isOver = overIndex === index && dragIndex !== index;
          const dayIndex = topic.workshopDayIndex;
          const showDayHeader =
            workshopMode &&
            typeof dayIndex === 'number' &&
            (index === 0 || topics[index - 1]?.workshopDayIndex !== dayIndex);
          const timing =
            typeof dayIndex === 'number' ? timingByDay.get(dayIndex) : undefined;
          const dayTopics =
            typeof dayIndex === 'number'
              ? topics.filter((t) => t.workshopDayIndex === dayIndex)
              : [];
          const dayMinutes = dayTopics.reduce((sum, t) => {
            const dayPre = t.preQuiz ? displayQuiz('pre') : undefined;
            const dayPost = t.postQuiz ? displayQuiz('post') : undefined;
            return (
              sum +
              getTopicBlockDuration(
                { ...t, preQuiz: dayPre, postQuiz: dayPost },
                questionBankOptions,
              )
            );
          }, 0);
          const dayEnd =
            timing && dayMinutes > 0
              ? addMinutesToDateTime(
                  timing.startDate,
                  timing.startTime,
                  dayMinutes,
                )
              : null;

          return (
            <div key={topic.id} className="d-flex flex-column gap-2">
              {showDayHeader && timing ? (
                <div className="rounded border border-warning-subtle bg-warning-subtle px-3 py-3">
                  <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                    <p className="mb-0 fw-semibold">
                      Day {(dayIndex ?? 0) + 1}
                      {dayEnd ? ` · ends ${dayEnd.endTime}` : ''}
                    </p>
                    <span className="small text-muted">
                      {dayTopics.length} topic{dayTopics.length === 1 ? '' : 's'} ·{' '}
                      {formatDurationMinutes(dayMinutes)}
                    </span>
                  </div>
                  {onDayScheduleChange ? (
                    <DateTimeField
                      label="Date / start time"
                      date={timing.startDate}
                      time={timing.startTime}
                      onDateChange={(value) =>
                        onDayScheduleChange(dayIndex!, { startDate: value })
                      }
                      onTimeChange={(value) =>
                        onDayScheduleChange(dayIndex!, { startTime: value })
                      }
                    />
                  ) : (
                    <p className="small text-muted mb-0">
                      {timing.startDate} · {timing.startTime}
                    </p>
                  )}
                </div>
              ) : null}

              <div
                draggable={!structureLocked}
                onDragStart={() => {
                  if (structureLocked) return;
                  setDragIndex(index);
                }}
                onDragEnd={clearDrag}
                onDragOver={(e) => {
                  if (structureLocked) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setOverIndex(index);
                }}
                onDrop={(e) => {
                  if (structureLocked) return;
                  e.preventDefault();
                  handleTopicDrop(index);
                }}
                className={`flex flex-col gap-0 transition-all ${
                  isDragging ? 'opacity-50' : ''
                } ${isOver ? 'rounded-2xl ring-2 ring-blue-200' : ''}`}
              >
              {/* No subtopics → topic-level quizzes */}
              {!topic.subtopics?.length && preQuiz && (
                <QuizBranchNode
                  quiz={preQuiz}
                  durationMinutes={getQuizDurationMinutes(preQuiz, questionBankOptions)}
                  onConfigure={() => openQuizEditor('pre')}
                  onRemove={() => removeQuiz(topic.id, 'pre')}
                />
              )}

              <article className="card mb-0">
                <div className="card-body d-flex flex-column flex-sm-row align-items-sm-start gap-3">
                  <div className="min-w-0 flex-grow-1">
                    <div className="mb-1 d-flex flex-wrap align-items-center gap-2">
                      <h6 className="mb-0">{topic.title}</h6>
                      {workshopMode && onChangeTopicDay && !structureLocked ? (
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto', minWidth: 96 }}
                          value={topic.workshopDayIndex ?? 0}
                          onChange={(e) =>
                            onChangeTopicDay(
                              topic.id,
                              Number(e.target.value),
                            )
                          }
                          aria-label={`Assign ${topic.title} to a workshop day`}
                        >
                          {Array.from(
                            { length: workshopDayCount },
                            (_, i) => (
                              <option key={i} value={i}>
                                Day {i + 1}
                              </option>
                            ),
                          )}
                        </select>
                      ) : workshopMode && structureLocked ? (
                        <span className="badge text-bg-light border">
                          Day {(topic.workshopDayIndex ?? 0) + 1}
                        </span>
                      ) : null}
                      {topic.subtopics?.length ? (
                        <span
                          className="badge text-bg-info d-inline-flex align-items-center gap-1"
                          title="Auto-calculated from subtopics"
                        >
                          <Clock size={12} />
                          {topic.durationMinutes}m (subtopics)
                        </span>
                      ) : (
                        <TopicDurationField
                          topicId={topic.id}
                          topicTitle={topic.title}
                          minutes={
                            isTopicDurationUnset(topic.durationMinutes)
                              ? null
                              : topic.durationMinutes
                          }
                          courseId={courseId}
                          onChange={(m) => updateTopicDuration(topic.id, m)}
                        />
                      )}
                      {hasQuizTime && (
                        <span className="badge text-bg-success-subtle text-success">
                          {blockMinutes}m total
                        </span>
                      )}
                    </div>
                    {topic.description ? (
                      <p className="mb-3 small text-muted">{topic.description}</p>
                    ) : null}

                    {/* When subtopics exist, render each as its own timeline block */}
                    {topic.subtopics?.length ? (
                      <div className="d-flex flex-column gap-0 mt-2">
                        {topic.subtopics.map((sub) => {
                          const subPreQuiz = sub.preQuiz ? displayQuiz('pre') : undefined;
                          const subPostQuiz = sub.postQuiz ? displayQuiz('post') : undefined;
                          return (
                            <div key={sub.id} className="d-flex flex-column gap-0">
                              {subPreQuiz && (
                                <QuizBranchNode
                                  quiz={subPreQuiz}
                                  durationMinutes={getQuizDurationMinutes(subPreQuiz, questionBankOptions)}
                                  onConfigure={() => openQuizEditor('pre')}
                                  onRemove={() => {
                                    updateTopic(topic.id, (t) => ({
                                      ...t,
                                      subtopics: t.subtopics?.map((s) =>
                                        s.id === sub.id ? { ...s, preQuiz: undefined } : s,
                                      ),
                                    }));
                                  }}
                                />
                              )}

                              <article className="card mb-0">
                                <div className="card-body d-flex align-items-center gap-3 py-2">
                                  <div className="min-w-0 flex-grow-1">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                      <h6 className="mb-0 small fw-semibold">{sub.title}</h6>
                                      <div className="d-flex align-items-center gap-1">
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          style={{ width: 64 }}
                                          min={1}
                                          value={sub.durationMinutes}
                                          aria-label={`${sub.title} duration minutes`}
                                          onChange={(e) => {
                                            const minutes = Math.max(
                                              1,
                                              parseInt(e.target.value, 10) || 1,
                                            );
                                            updateTopicSubtopics(
                                              topic.id,
                                              (topic.subtopics ?? []).map((s) =>
                                                s.id === sub.id
                                                  ? { ...s, durationMinutes: minutes }
                                                  : s,
                                              ),
                                            );
                                          }}
                                        />
                                        <span className="text-muted small">min</span>
                                      </div>
                                    </div>
                                    {!sub.preQuiz && (
                                      <div className="mt-1">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            updateTopic(topic.id, (t) => ({
                                              ...t,
                                              subtopics: t.subtopics?.map((s) =>
                                                s.id === sub.id
                                                  ? { ...s, preQuiz: { slot: 'pre' as const, mode: 'level-rules' as const } }
                                                  : s,
                                              ),
                                            }));
                                          }}
                                          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                                        >
                                          <Plus size={14} />
                                          Add Pre-Quiz (Optional)
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </article>

                              {subPostQuiz && (
                                <QuizBranchNode
                                  quiz={subPostQuiz}
                                  durationMinutes={getQuizDurationMinutes(subPostQuiz, questionBankOptions)}
                                  onConfigure={() => openQuizEditor('post')}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="d-flex gap-2 flex-wrap">
                        {!topic.preQuiz && (
                          <button
                            type="button"
                            onClick={() => addPreQuiz(topic.id)}
                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                          >
                            <Plus size={14} />
                            Add Pre-Quiz (Optional)
                          </button>
                        )}
                        {!structureLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            toggleSubtopics(topic.id);
                            if (!topic.subtopics?.length && !expandedSubtopics.has(topic.id)) {
                              updateTopicSubtopics(topic.id, [
                                {
                                  id: `sub-${Date.now()}-0`,
                                  index: 0,
                                  title: '',
                                  durationMinutes: 0,
                                },
                              ]);
                            }
                          }}
                          className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                        >
                          {expandedSubtopics.has(topic.id) ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronRight size={14} />
                          )}
                          Subtopics
                        </button>
                        )}
                      </div>
                    )}

                    {!topic.subtopics?.length && expandedSubtopics.has(topic.id) && (
                      <SubtopicList
                        subtopics={topic.subtopics ?? []}
                        onChange={(subs) => updateTopicSubtopics(topic.id, subs)}
                        structureLocked={structureLocked}
                      />
                    )}
                  </div>
                  {!structureLocked && (
                  <GripVertical
                    className="text-muted"
                    size={20}
                    aria-label="Drag to reorder topic"
                    style={{ cursor: 'grab' }}
                  />
                  )}
                </div>
              </article>

              {/* No subtopics → topic-level post quiz */}
              {!topic.subtopics?.length && postQuiz && (
                <QuizBranchNode
                  quiz={postQuiz}
                  durationMinutes={getQuizDurationMinutes(postQuiz, questionBankOptions)}
                  onConfigure={() => openQuizEditor('post')}
                />
              )}
              </div>
            </div>
          );
        })}
      </div>

      {quizEditor && (
        <TopicQuizModal
          open
          topicId="global"
          slot={quizEditor.slot}
          topicTitle="All sessions & topics"
          topicIndex={0}
          initialConfig={quizEditor.config}
          secondsByLevel={quizDefaults?.secondsByLevel}
          onClose={() => setQuizEditor(null)}
          onSave={(config) => {
            void saveQuizConfig(config);
          }}
        />
      )}
    </div>
  );
}
